import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12 } from "styled-components";
import React from "react";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Eyebrow } from "~/features/ui/eyebrow";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type TextFactsMediaFact = {
  title?: string | null;
  label?: string | null;
};
export type TextFactsMediaBlockViewProps<TFact extends TextFactsMediaFact = TextFactsMediaFact> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  facts?: TFact[] | null;
  getFactKey?: (fact: TFact, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function TextFactsMediaBlockView<TFact extends TextFactsMediaFact = TextFactsMediaFact>({
  anchor,
  eyebrow,
  facts = [],
  getFactKey,
  hasTopDivider = true,
  headline,
  mediaDesktop,
  mediaMobile,
  mode = "light",
  sectionProps,
  theme = "white"
}: TextFactsMediaBlockViewProps<TFact>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // v3: white theme renders true white (helper maps "white" to grey-20).
  const surface = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, surface]}>
      <Wrap mode={mode as Mode}>
        <_StyledGrid>
          <_StyledDiv $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "gridColumn": "span 4 / span 4",
          "paddingTop": "4rem",
          "paddingBottom": "4rem"
        }, surface]}>
            <_StyledDiv2>
              <_StyledDiv3>
                {eyebrow && <Eyebrow text={eyebrow} mode={mode as Mode} />}
                {headline && <_StyledHeadline type="h2" text={headline} />}
              </_StyledDiv3>
              <_StyledDiv4 $_css3={[{
              "marginTop": "4rem",
              "display": "block",
              "borderTopWidth": "1px",
              "borderBottomWidth": "1px",
              "padding": "1.6rem",
              "@media (min-width: 1024px)": {
                "display": "none"
              }
            }, themeStyle.border]}>{mediaMobile}</_StyledDiv4>
              {hasItems(facts) && <_StyledDiv5>
                  {facts.map((fact, idx) => <div key={getFactKey?.(fact, idx) ?? idx}>
                      {fact.title && <_StyledP>{fact.title}</_StyledP>}
                      {fact.label && <_StyledP2 $_css4={[{
                  "marginTop": "0.4rem",
                  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
                  "fontSize": "1.4rem",
                  "lineHeight": "1.1",
                  "letterSpacing": "0.028000000000000004rem",
                  "fontWeight": "400",
                  "@media (min-width: 1024px)": {
                    "marginTop": "0.8rem",
                    "fontSize": "1.6rem",
                    "lineHeight": "1.1",
                    "letterSpacing": "0.032rem",
                    "fontWeight": "400"
                  }
                }, themeStyle.label]}>
                          {fact.label}
                        </_StyledP2>}
                    </div>)}
                </_StyledDiv5>}
            </_StyledDiv2>
          </_StyledDiv>
          <_StyledDiv6 $_css5={[{
          "gridColumn": "span 8 / span 8",
          "display": "none",
          "alignItems": "center",
          "borderLeftWidth": "1px",
          "padding": "4rem",
          "@media (min-width: 1024px)": {
            "display": "flex"
          }
        }, themeStyle.border, surface]}>
            <_StyledDiv7>{mediaDesktop}</_StyledDiv7>
          </_StyledDiv6>
        </_StyledGrid>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-l7sqwl-0"
})`${p => p.$_css}`;
var _StyledGrid = _styled2(Grid).withConfig({
  componentId: "sc-sf-l7sqwl-1"
})({
  "@media (min-width: 1024px)": {
    "minHeight": "688px"
  }
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-l7sqwl-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-l7sqwl-3"
})({
  "display": "flex",
  "height": "100%",
  "flexDirection": "column",
  "rowGap": "0.8rem",
  "@media (min-width: 1024px)": {
    "rowGap": "8rem"
  }
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-l7sqwl-4"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "0.8rem",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "8rem",
    "paddingRight": "8rem"
  }
});
var _StyledHeadline = _styled6(Headline).withConfig({
  componentId: "sc-sf-l7sqwl-5"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-l7sqwl-6"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-l7sqwl-7"
})({
  "marginTop": "auto",
  "display": "flex",
  "flexDirection": "column",
  "gap": "2.4rem",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "paddingTop": "1.6rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "8rem",
    "paddingRight": "8rem"
  }
});
var _StyledP = _styled9("p").withConfig({
  componentId: "sc-sf-l7sqwl-8"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledP2 = _styled10("p").withConfig({
  componentId: "sc-sf-l7sqwl-9"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled11("div").withConfig({
  componentId: "sc-sf-l7sqwl-10"
})`${p => p.$_css5}`;
var _StyledDiv7 = /*#__PURE__*/_styled12("div").withConfig({
  displayName: "text-facts-media___StyledDiv7",
  componentId: "sc-1kmzl53-0"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});